import { asPlaybookId } from "@repo/app-types/entities/playbook";
import type { SignalsOverview } from "@repo/app-types/entities/signal";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { IconPlus } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { useSignalsOverviewQuery } from "../../../api/entities/signal.js";
import { appPaths } from "../../../app/appPaths.js";
import {
  PageBody,
  PageLayout,
  TableCard,
} from "../../../components/layout/pageLayout.js";
import { PerformancePlaybookSwitcher } from "../../../components/performancePlaybookSwitcher.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { buildSignalFilterQueryArgs } from "../../filters/buildFilterQueryArgs.js";
import { useFilters } from "../../filters/filters.js";
import { usePlaybookFilterDefaults } from "../../filters/usePlaybookFilterDefaults.js";
import {
  DeltaPill,
  formatDeltaPoints,
} from "../../teammatePerformance/monthInReview/deltaPill.js";
import { TrendChart } from "../../teammatePerformance/trendChart.js";
import { computePercent } from "../signalStatsFormatters.js";

type SignalRowEntity = SignalsOverview & { id: string };

export const SignalsPage: FC = () => {
  const { playbookId: playbookIdParam } = useParams();
  if (!playbookIdParam) {
    throw new Error("Missing playbookId param");
  }
  const playbookId = asPlaybookId(playbookIdParam);
  const navigate = useNavigate();

  const playbookFilterDefaults = usePlaybookFilterDefaults(playbookId, "team");
  const [
    {
      dateRange,
      dateBasis,
      teammateGroupIds,
      outcome,
      customFields,
      totalRecordingDuration,
      recordingCount,
      playbookConditions,
    },
    renderFilters,
  ] = useFilters(
    [
      "teammateGroupIds",
      "dateRange",
      "dateBasis",
      "outcome",
      "customFields",
      "totalRecordingDuration",
      "recordingCount",
      "playbookConditions",
    ],
    { playbookId, view: "team" },
    playbookFilterDefaults,
  );

  const now = useNow();

  const { scope, filters } = useMemo(
    () =>
      buildSignalFilterQueryArgs(now, {
        playbookId,
        dateRange,
        dateBasis,
        teammateGroupIds: teammateGroupIds ?? null,
        outcome,
        customFields,
        signalId: null,
        signalMetadataFields: [],
        totalRecordingDuration,
        recordingCount,
        playbookConditions,
      }),
    [
      now,
      playbookId,
      dateRange,
      dateBasis,
      teammateGroupIds,
      outcome,
      customFields,
      totalRecordingDuration,
      recordingCount,
      playbookConditions,
    ],
  );

  const {
    data: stats,
    isLoading: isStatsLoading,
    isFetching: isStatsFetching,
  } = useSignalsOverviewQuery({
    scope,
    filters,
  });

  const priorBySignal = useMemo(() => {
    const map = new Map(stats?.prior.map((s) => [s.signalId, s]));
    return map;
  }, [stats?.prior]);

  const dateRangeDays = Math.round(dateRange.length.as("days"));
  const isRelativeRange = dateRange.end === undefined;

  const signalEntities: readonly SignalRowEntity[] | undefined = useMemo(
    () => stats?.current.map((s) => ({ ...s, id: s.signalId })),
    [stats],
  );

  const columns: readonly EntityTableColumnDefinition<SignalRowEntity>[] =
    useMemo(
      () => [
        {
          id: "signal",
          header: "Signal",
          isRowHeader: true,
          getTextValue: (row) => row.signalName,
          renderCell: (row) => (
            <div>
              <p className="font-semibold text-foreground">{row.signalName}</p>
              <p className="mt-0.5 line-clamp-1 text-sm text-muted-foreground">
                {row.signalDescription}
              </p>
            </div>
          ),
        },
        {
          id: "matchRate",
          header: `Match rate (${dateRangeDays}d)`,
          renderCell: (row) => {
            const currentRate = computePercent(
              row.totalMatchCount,
              row.totalInteractionCount,
            );
            return (
              <div>
                <p className="font-semibold text-foreground">{currentRate}%</p>
                <p className="text-sm text-muted-foreground">
                  {row.totalMatchCount} of {row.totalInteractionCount} calls
                </p>
              </div>
            );
          },
        },
        {
          id: "delta",
          header: "Delta",
          renderCell: (row) => {
            const prior = priorBySignal.get(row.signalId);
            const currentRate = computePercent(
              row.totalMatchCount,
              row.totalInteractionCount,
            );
            const priorRate = computePercent(
              prior?.totalMatchCount ?? 0,
              prior?.totalInteractionCount ?? 0,
            );
            const delta = formatDeltaPoints(currentRate, priorRate);
            return <DeltaPill delta={delta.delta} tone={delta.tone} />;
          },
        },
        {
          id: "sparkline",
          header: "Trend",
          renderCell: (row) => (
            <TrendChart
              className="h-8"
              lines={[
                {
                  key: "matches",
                  data: row.buckets.map((b) => b.matchCount),
                },
              ]}
              minYRange={1}
              renderValue={(value) => String(value)}
              showsXAxisLabels={false}
              showsYAxisLabels={false}
              getColorClassName={() => "text-gray-600"}
              timeUnit="day"
              getTooltipDateLabel={
                isRelativeRange
                  ? undefined
                  : (index) => {
                      const bucket = row.buckets[index];
                      return bucket
                        ? DateTime.fromISO(bucket.bucketStart).toFormat(
                            "MMM d, yyyy",
                          )
                        : "";
                    }
              }
            />
          ),
        },
      ],
      [dateRangeDays, priorBySignal, isRelativeRange],
    );

  return (
    <PageLayout>
      <PageHeader
        title="Signals"
        actions={
          <Can
            I={PermissionActions.CREATE}
            a={PermissionSubjects.SIGNALS}
            passThrough={true}
          >
            {(allowed) => (
              <Button
                variant={ButtonVariant.Primary}
                size={Size.SMALL}
                icon={<IconPlus className="size-4" />}
                isDisabled={!allowed}
                onClick={() => navigate(appPaths.settingsNewSignal.build())}
              >
                New signal
              </Button>
            )}
          </Can>
        }
        filterBar={renderFilters({
          isLoading: isStatsFetching,
          leadingItems: <PerformancePlaybookSwitcher />,
        })}
      />

      <PageBody>
        <TableCard>
          <EntityTable
            aria-label="Signals"
            size={Size.MEDIUM}
            columns={columns}
            entities={signalEntities}
            isLoading={isStatsLoading}
            isFullBleed
            emptyMessage="No signals have been created yet."
            onEntityAction={(entity) =>
              navigate(`../signals/${encodeURIComponent(entity.signalId)}`)
            }
          />
        </TableCard>
      </PageBody>
    </PageLayout>
  );
};
